import React from 'react';
import {
  AppButton,
  Box,
  FieldWrapper,
  Hint,
  Inscription,
  Label,
  Preloader,
  RadioButton,
  Text,
  WarningCard,
} from '@common-fe/common-fe';

import { SuccessModal } from '@/components/elements';
import { ModalWrapper } from '@/components/wrappers';

import useReviewAddressModal, { ReviewOption } from '../hooks/useReviewAddressModal';

import EditEmployeeAddressModal from './EditEmployeeAddressModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const ReviewAddressModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const {
    selectedOption,
    setSelectedOption,
    isEditModalVisible,
    isSuccessVisible,
    successMessage,
    isConfirming,
    hasEditPermission,
    submitButtonLabel,
    isSubmitDisabled,
    formattedAddress,
    addressPayload,
    handleSubmit,
    handleClose,
    handleEditModalClose,
    handleEditSuccess,
    handleSuccessClose,
  } = useReviewAddressModal({ onClose });

  return (
    <>
      <ModalWrapper
        testId="review-address-modal"
        visible={isOpen}
        onSetVisible={handleClose}
        title="Review Address"
        isClosingByClickOutsideBlocked
      >
        <Box pad="spacing24" round="moduleRound" background="module">
          <Box
            pad="spacing24"
            background="canvas"
            round="container1Round"
            border={{ color: 'border2', size: '1px' }}
            margin={{ bottom: 'spacing24' }}
            data-testid="review-address-modal_address-section"
          >
            <FieldWrapper direction="row" align="center">
              <Label name="currentAddress" isNarrow>
                Current address
              </Label>
              <Text color="textBody">
                {formattedAddress || '—'}
              </Text>
            </FieldWrapper>
          </Box>

          <Box
            pad="spacing24"
            background="canvas"
            round="container1Round"
            border={{ color: 'border2', size: '1px' }}
          >
            <FieldWrapper direction="row" align="start">
              <Label name="addressReview" isNarrow>
                Is the address listed above correct?
              </Label>
              <Box gap="spacing12">
                <RadioButton
                  data-testid="review-address-modal_option-correct"
                  name="addressReview"
                  checked={selectedOption === ReviewOption.CORRECT}
                  onChange={() => setSelectedOption(ReviewOption.CORRECT)}
                  label={(
                    <Box height="24px" align="center">
                      <Text color="textBody" margin={{ vertical: 'auto' }}>
                        Yes, its correct
                      </Text>
                    </Box>
                  )}
                />
                <Hint
                  testId="review-address-modal_option-update-hint"
                  hideHint={hasEditPermission}
                  hintElement={(
                    <RadioButton
                      data-testid="review-address-modal_option-update"
                      name="addressReview"
                      disabled={!hasEditPermission}
                      checked={selectedOption === ReviewOption.NEEDS_UPDATE}
                      onChange={() => setSelectedOption(ReviewOption.NEEDS_UPDATE)}
                      label={(
                        <Box height="24px" align="center">
                          <Text
                            color={hasEditPermission ? 'textBody' : 'textDisabled'}
                            margin={{ vertical: 'auto' }}
                          >
                            No, needs to be updated
                          </Text>
                        </Box>
                      )}
                    />
                  )}
                >
                  <Inscription size="small" textAlign="center">
                    You don&apos;t have permission to do this
                  </Inscription>
                </Hint>
              </Box>
            </FieldWrapper>
          </Box>

          {hasEditPermission && (
            <WarningCard
              testId="review-address-modal"
              title={(
                <Text weight={700}>
                  It’s important all data sources are updated.
                </Text>
              )}
              description="Any census updates through file or api will overwrite these changes."
              backgroundColor="infoContainer"
              borderColor="infoBorder"
              topBorderColor="info"
              topBorderMargin="spacing12"
              margin={{ top: 'spacing24' }}
              containerProps={{ pad: 'spacing24' }}
            />
          )}
        </Box>

        <Box direction="row" justify="end" gap="spacing12" margin={{ top: 'spacing24' }}>
          <AppButton
            testId="review-address-modal_cancel-btn"
            onClick={handleClose}
            buttonType="secondary"
            width="180px"
          >
            Cancel
          </AppButton>
          <AppButton
            testId="review-address-modal_submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            width="180px"
          >
            {isConfirming ? <Preloader color="white" /> : submitButtonLabel}
          </AppButton>
        </Box>
      </ModalWrapper>

      {isEditModalVisible && (
        <EditEmployeeAddressModal
          visible={isEditModalVisible}
          onClose={handleEditModalClose}
          onSuccess={handleEditSuccess}
          address={addressPayload}
        />
      )}

      <SuccessModal
        testId="review-address-success-modal"
        visible={isSuccessVisible}
        onSetVisible={handleSuccessClose}
        header={successMessage}
        helptext="The returned mail will be mailed to you shortly."
        buttonText="Got it!"
      />
    </>
  );
};

export default ReviewAddressModal;
